import { HttpClient, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';

import { SessionService } from '@rpgc-ai/services/auth/session.service';
import {
  PUBLIC_API_REQUEST,
  SKIP_AUTH_ERROR_HANDLING,
  SKIP_GLOBAL_ERROR_NOTIFICATION,
} from '@rpgc-ai/services/auth/skip-global-error-notification.token';
import { SeoService } from '@rpgc-ai/services/seo/seo.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import type { ICharacter } from '@rpgc-ai/shared-interfaces/interfaces/characters.interface';
import type { ELanguage } from '@rpgc-ai/shared-interfaces/interfaces/language.interface';
import { buildCharacterPageTitle } from '@rpgc-ai/shared-interfaces/utils/character-page-title.util';
import { getLocalizedPublicName } from '@rpgc-ai/shared-interfaces/utils/localized-character-name.util';

import { environment } from '@rpgc-ai/environments';

import { catchError, map, of, tap } from 'rxjs';

/**
 * Оба слага вместе адресуют карточку в канонической форме `view-chatbot/<authorSlug>/<characterSlug>`;
 * короткая форма маршрута их не содержит, поэтому резолвер публикует пару в `data.seo`.
 */
export interface IChatbotCanonicalSlugs {
  readonly characterSlug: string;
  readonly authorSlug: string;
}

export const chatbotSeoResolver: ResolveFn<IChatbotCanonicalSlugs | undefined> = (route) => {
  const http = inject(HttpClient);
  const seoService = inject(SeoService);
  const languageService = inject(LanguageService);
  const sessionService = inject(SessionService);

  const botId = route.params['botId'];
  if (!botId) {
    return;
  }

  const creatorSlug = route.params['creatorSlug'];
  const characterUrl = creatorSlug ? `${creatorSlug}/${botId}` : botId;

  const lang = languageService.getLanguageForRoute(route).toUpperCase() as ELanguage;
  const isAuthenticated = sessionService.isAuthenticated() === true;
  const context = (isAuthenticated
    ? new HttpContext().set(SKIP_AUTH_ERROR_HANDLING, true)
    : new HttpContext().set(PUBLIC_API_REQUEST, true)
  ).set(SKIP_GLOBAL_ERROR_NOTIFICATION, true);

  return http.get<ICharacter>(`characters/${characterUrl}`, { context }).pipe(
    tap((character) => {
      const title = buildCharacterPageTitle(getLocalizedPublicName(character, lang), environment.titleSuffix);
      const descriptions = character.descriptions ?? {};
      const description = descriptions[lang] ?? descriptions['EN'] ?? Object.values(descriptions)[0] ?? '';

      seoService.setTitle(title);
      seoService.setTag('description', description, 'name');
      seoService.setTag('og:title', title, 'property');
      seoService.setTag('og:description', description, 'property');
    }),
    map(({ slug, userSlug }) => ({ characterSlug: slug, authorSlug: userSlug })),
    catchError((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('chatbotSeoResolver: failed to load chatbot SEO data', error);
      return of(void 0);
    }),
  );
};
